import { useState, useMemo, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateTaskQueries, getDisplayName } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarSrc } from "@/components/user-avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PriorityBadge } from "@/components/tasks/priority-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  MoreHorizontal,
  Archive,
  UserCircle,
  PawPrint,
  Link2,
  CheckSquare,
  MessageSquare,
  Paperclip,
  Calendar,
  GripVertical,
  X,
  Copy,
} from "lucide-react";
import { LinkedAnimalsTooltip } from "@/components/tasks/linked-animals-tooltip";

interface TaskListItem {
  id: string;
  boardId: string;
  title: string;
  description: string | null;
  statusId: string;
  rank: number;
  assigneeUserId: string | null;
  dueDate: string | null;
  priority: string | null;
  createdAt: string;
  updatedAt: string;
  statusName: string;
  statusSortOrder: number;
  assigneeUsername: string | null;
  assigneeFirstName: string | null;
  assigneeLastName: string | null;
  assigneeAvatarUrl: string | null;
  animalCount: number;
  firstAnimalName: string | null;
  interactionCount: number;
  checklist: { done: number; total: number };
  commentCount: number;
  attachmentCount?: number;
  labels: Array<{ labelId: string; labelName: string; labelColor: string | null }>;
}

interface Status {
  id: string;
  name: string;
  isArchived: boolean;
  sortOrder: number;
}

interface BoardLabel {
  id: string;
  name: string;
  color: string | null;
  isArchived: boolean;
}

interface BoardMember {
  userId: string;
  role: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
}

interface TaskKanbanViewProps {
  tasks: TaskListItem[];
  statuses: Status[];
  members: BoardMember[];
  labels: BoardLabel[];
  canEdit: boolean;
  onTaskClick: (taskId: string) => void;
  onCreateTask: (statusId?: string) => void;
  onDuplicate?: (taskId: string) => void;
}

function isOverdue(dueDate: string | null): boolean {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dueDate) < today;
}

function getInitials(name: string | null, username: string | null): string {
  if (name) return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  if (username) return username.slice(0, 2).toUpperCase();
  return "?";
}

function SortableTaskCard({
  task,
  canEdit,
  statuses,
  members,
  onTaskClick,
  onChangeStatus,
  onChangeAssignee,
  onArchive,
  onDuplicate,
}: {
  task: TaskListItem;
  canEdit: boolean;
  statuses: Status[];
  members: BoardMember[];
  onTaskClick: (taskId: string) => void;
  onChangeStatus: (taskId: string, statusId: string) => void;
  onChangeAssignee: (taskId: string, assigneeUserId: string | null) => void;
  onArchive: (taskId: string) => void;
  onDuplicate?: (taskId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: !canEdit });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <TaskCardContent
        task={task}
        canEdit={canEdit}
        statuses={statuses}
        members={members}
        onTaskClick={onTaskClick}
        onChangeStatus={onChangeStatus}
        onChangeAssignee={onChangeAssignee}
        onArchive={onArchive}
        onDuplicate={onDuplicate}
        dragListeners={listeners}
      />
    </div>
  );
}

function TaskCardContent({
  task,
  canEdit,
  statuses,
  members,
  onTaskClick,
  onChangeStatus,
  onChangeAssignee,
  onArchive,
  onDuplicate,
  dragListeners,
  isOverlay,
}: {
  task: TaskListItem;
  canEdit: boolean;
  statuses: Status[];
  members: BoardMember[];
  onTaskClick: (taskId: string) => void;
  onChangeStatus: (taskId: string, statusId: string) => void;
  onChangeAssignee: (taskId: string, assigneeUserId: string | null) => void;
  onArchive: (taskId: string) => void;
  onDuplicate?: (taskId: string) => void;
  dragListeners?: Record<string, any>;
  isOverlay?: boolean;
}) {
  const assigneeName = task.assigneeUserId
    ? getDisplayName(task.assigneeFirstName, task.assigneeLastName, task.assigneeUsername)
    : null;

  return (
    <Card
      className={`p-3 cursor-pointer hover-elevate ${isOverlay ? "shadow-lg ring-2 ring-primary/20" : ""}`}
      data-testid={`task-kanban-card-${task.id}`}
      onClick={() => onTaskClick(task.id)}
    >
      <div className="flex items-start gap-2">
        {canEdit && dragListeners && (
          <button
            className="mt-0.5 text-muted-foreground cursor-grab active:cursor-grabbing shrink-0"
            data-testid={`task-drag-handle-${task.id}`}
            {...dragListeners}
            onClick={e => e.stopPropagation()}
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-start justify-between gap-1">
            <p className="text-sm font-medium leading-tight truncate" data-testid={`task-kanban-title-${task.id}`}>
              {task.title}
            </p>
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    data-testid={`task-kanban-actions-${task.id}`}
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
                  <DropdownMenuItem className="text-xs font-medium text-muted-foreground" disabled>
                    Change Status
                  </DropdownMenuItem>
                  {statuses.filter(s => s.id !== task.statusId).map(s => (
                    <DropdownMenuItem
                      key={s.id}
                      data-testid={`task-kanban-status-${task.id}-${s.id}`}
                      onClick={() => onChangeStatus(task.id, s.id)}
                    >
                      {s.name}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-xs font-medium text-muted-foreground" disabled>
                    Assign To
                  </DropdownMenuItem>
                  {members.map(m => (
                    <DropdownMenuItem
                      key={m.userId}
                      data-testid={`task-kanban-assign-${task.id}-${m.userId}`}
                      onClick={() => onChangeAssignee(task.id, m.userId)}
                    >
                      <UserCircle className="w-3 h-3 mr-1" />
                      {getDisplayName(m.firstName, m.lastName, m.username)}
                    </DropdownMenuItem>
                  ))}
                  {task.assigneeUserId && (
                    <DropdownMenuItem
                      data-testid={`task-kanban-unassign-${task.id}`}
                      onClick={() => onChangeAssignee(task.id, null)}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Unassign
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  {onDuplicate && (
                    <DropdownMenuItem
                      data-testid={`task-kanban-duplicate-${task.id}`}
                      onClick={() => onDuplicate(task.id)}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Duplicate
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid={`task-kanban-archive-${task.id}`}
                    onClick={() => onArchive(task.id)}
                    className="text-destructive"
                  >
                    <Archive className="w-3 h-3 mr-1" />
                    Archive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {task.priority && (
            <PriorityBadge priority={task.priority} className="text-[10px] px-1.5 py-0" />
          )}

          {task.labels.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {task.labels.map(l => (
                <Badge
                  key={l.labelId}
                  variant="outline"
                  className="text-[10px] px-1.5 py-0"
                  data-testid={`task-kanban-label-${task.id}-${l.labelId}`}
                >
                  {l.labelColor && (
                    <span
                      className="w-2 h-2 rounded-full mr-1 inline-block shrink-0"
                      style={{ backgroundColor: l.labelColor }}
                    />
                  )}
                  {l.labelName}
                </Badge>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {assigneeName && (
              <div className="flex items-center gap-1" data-testid={`task-kanban-assignee-${task.id}`}>
                <Avatar className="h-5 w-5">
                  {task.assigneeAvatarUrl && (
                    <AvatarImage src={getAvatarSrc(task.assigneeAvatarUrl, task.assigneeUserId)} alt={assigneeName} />
                  )}
                  <AvatarFallback className="text-[9px]">
                    {getInitials(assigneeName, task.assigneeUsername)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                  {assigneeName}
                </span>
              </div>
            )}

            {task.dueDate && (
              <span
                className={`flex items-center gap-0.5 text-xs ${isOverdue(task.dueDate) ? "text-destructive font-medium" : "text-muted-foreground"}`}
                data-testid={`task-kanban-due-${task.id}`}
              >
                <Calendar className="w-3 h-3" />
                {new Date(task.dueDate + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            )}
          </div>

          {(task.animalCount > 0 || task.interactionCount > 0 || task.checklist.total > 0 || task.commentCount > 0 || (task.attachmentCount ?? 0) > 0) && (
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              {task.animalCount > 0 && (
                <LinkedAnimalsTooltip taskId={task.id} animalCount={task.animalCount}>
                  <span className="flex items-center gap-0.5" data-testid={`task-kanban-animals-${task.id}`}>
                    <PawPrint className="w-3 h-3" />
                    <span className="truncate max-w-[60px]">{task.firstAnimalName || "Animal"}</span>
                    {task.animalCount > 1 && <span>+{task.animalCount - 1}</span>}
                  </span>
                </LinkedAnimalsTooltip>
              )}
              {task.interactionCount > 0 && (
                <span className="flex items-center gap-0.5" data-testid={`task-kanban-interactions-${task.id}`}>
                  <Link2 className="w-3 h-3" />{task.interactionCount}
                </span>
              )}
              {task.checklist.total > 0 && (
                <span className="flex items-center gap-0.5" data-testid={`task-kanban-checklist-${task.id}`}>
                  <CheckSquare className="w-3 h-3" />{task.checklist.done}/{task.checklist.total}
                </span>
              )}
              {task.commentCount > 0 && (
                <span className="flex items-center gap-0.5" data-testid={`task-kanban-comments-${task.id}`}>
                  <MessageSquare className="w-3 h-3" />{task.commentCount}
                </span>
              )}
              {(task.attachmentCount ?? 0) > 0 && (
                <span className="flex items-center gap-0.5" data-testid={`task-kanban-attachments-${task.id}`}>
                  <Paperclip className="w-3 h-3" />{task.attachmentCount}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function KanbanColumn({
  status,
  tasks,
  canEdit,
  statuses,
  members,
  onTaskClick,
  onCreateTask,
  onChangeStatus,
  onChangeAssignee,
  onArchive,
  onDuplicate,
}: {
  status: Status;
  tasks: TaskListItem[];
  canEdit: boolean;
  statuses: Status[];
  members: BoardMember[];
  onTaskClick: (taskId: string) => void;
  onCreateTask: (statusId?: string) => void;
  onChangeStatus: (taskId: string, statusId: string) => void;
  onChangeAssignee: (taskId: string, assigneeUserId: string | null) => void;
  onArchive: (taskId: string) => void;
  onDuplicate?: (taskId: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status.id });

  return (
    <div
      className="flex flex-col shrink-0 w-[300px]"
      data-testid={`task-kanban-column-${status.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3 px-1">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold" data-testid={`task-kanban-column-title-${status.id}`}>
            {status.name}
          </h3>
          <Badge variant="secondary" className="text-xs" data-testid={`task-kanban-column-count-${status.id}`}>
            {tasks.length}
          </Badge>
        </div>
        {canEdit && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onCreateTask(status.id)}
            data-testid={`task-kanban-add-task-${status.id}`}
          >
            <Plus className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div
        ref={setNodeRef}
        className={`flex-1 rounded-md transition-colors ${isOver ? "bg-accent/50" : ""}`}
      >
        <ScrollArea className="h-[calc(100vh-320px)]">
          <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2 p-1 min-h-[60px]">
              {tasks.length === 0 ? (
                canEdit ? (
                  <button
                    className="flex items-center justify-center w-full h-[60px] border-2 border-dashed rounded-md text-muted-foreground text-sm hover-elevate cursor-pointer"
                    data-testid={`task-kanban-empty-${status.id}`}
                    onClick={() => onCreateTask(status.id)}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Create a task
                  </button>
                ) : (
                  <div
                    className="flex items-center justify-center h-[60px] border-2 border-dashed rounded-md text-muted-foreground text-sm"
                    data-testid={`task-kanban-empty-${status.id}`}
                  >
                    No tasks
                  </div>
                )
              ) : (
                tasks.map(task => (
                  <SortableTaskCard
                    key={task.id}
                    task={task}
                    canEdit={canEdit}
                    statuses={statuses}
                    members={members}
                    onTaskClick={onTaskClick}
                    onChangeStatus={onChangeStatus}
                    onChangeAssignee={onChangeAssignee}
                    onArchive={onArchive}
                    onDuplicate={onDuplicate}
                  />
                ))
              )}
            </div>
          </SortableContext>
        </ScrollArea>
      </div>
    </div>
  );
}

export function TaskKanbanView({
  tasks,
  statuses,
  members,
  labels,
  canEdit,
  onTaskClick,
  onCreateTask,
  onDuplicate,
}: TaskKanbanViewProps) {
  const { toast } = useToast();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [localTasks, setLocalTasks] = useState<TaskListItem[]>([]);
  const [isOptimistic, setIsOptimistic] = useState(false);

  const workingTasks = isOptimistic ? localTasks : tasks;

  const sortedStatuses = useMemo(() =>
    [...statuses].sort((a, b) => a.sortOrder - b.sortOrder),
    [statuses],
  );

  const columnTasks = useMemo(() => {
    const map = new Map<string, TaskListItem[]>();
    for (const s of sortedStatuses) {
      map.set(s.id, []);
    }
    for (const t of workingTasks) {
      const col = map.get(t.statusId);
      if (col) col.push(t);
    }
    Array.from(map.values()).forEach((col) => {
      col.sort((a: TaskListItem, b: TaskListItem) => a.rank - b.rank);
    });
    return map;
  }, [workingTasks, sortedStatuses]);

  const activeTask = useMemo(() =>
    activeId ? workingTasks.find(t => t.id === activeId) ?? null : null,
    [activeId, workingTasks],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const moveMutation = useMutation({
    mutationFn: async (params: { taskId: string; statusId?: string; beforeTaskId?: string; afterTaskId?: string }) => {
      const { taskId, ...body } = params;
      return await apiRequest("POST", `/api/tasks/${taskId}/move`, body);
    },
    onSuccess: () => {
      setIsOptimistic(false);
      invalidateTaskQueries();
    },
    onError: (err: any) => {
      setIsOptimistic(false);
      invalidateTaskQueries();
      toast({ title: "Failed to move task", description: err.message, variant: "destructive" });
    },
  });

  const changeStatusMutation = useMutation({
    mutationFn: async (params: { taskId: string; statusId: string }) => {
      return await apiRequest("POST", `/api/tasks/${params.taskId}/move`, { statusId: params.statusId });
    },
    onSuccess: () => {
      invalidateTaskQueries();
      toast({ title: "Status updated" });
    },
    onError: (err: any) => {
      toast({ title: "Failed to update status", description: err.message, variant: "destructive" });
    },
  });

  const changeAssigneeMutation = useMutation({
    mutationFn: async (params: { taskId: string; assigneeUserId: string | null }) => {
      return await apiRequest("PATCH", `/api/tasks/${params.taskId}`, { assigneeUserId: params.assigneeUserId });
    },
    onSuccess: () => {
      invalidateTaskQueries();
      toast({ title: "Assignee updated" });
    },
    onError: (err: any) => {
      toast({ title: "Failed to update assignee", description: err.message, variant: "destructive" });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (taskId: string) => {
      return await apiRequest("PATCH", `/api/tasks/${taskId}/archive`, {});
    },
    onSuccess: () => {
      invalidateTaskQueries();
      toast({ title: "Task archived" });
    },
    onError: (err: any) => {
      toast({ title: "Failed to archive task", description: err.message, variant: "destructive" });
    },
  });

  const handleChangeStatus = useCallback((taskId: string, statusId: string) => {
    changeStatusMutation.mutate({ taskId, statusId });
  }, [changeStatusMutation]);

  const handleChangeAssignee = useCallback((taskId: string, assigneeUserId: string | null) => {
    changeAssigneeMutation.mutate({ taskId, assigneeUserId });
  }, [changeAssigneeMutation]);

  const handleArchive = useCallback((taskId: string) => {
    archiveMutation.mutate(taskId);
  }, [archiveMutation]);

  function findColumnForTask(taskId: string): string | null {
    for (const [statusId, col] of Array.from(columnTasks.entries())) {
      if (col.some((t: TaskListItem) => t.id === taskId)) return statusId;
    }
    return null;
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
    setLocalTasks([...tasks]);
    setIsOptimistic(true);
  }

  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over || !active) return;

    const activeTaskId = active.id as string;
    const overId = over.id as string;

    const fromCol = findColumnForTask(activeTaskId);
    const isOverColumn = sortedStatuses.some(s => s.id === overId);
    const toCol = isOverColumn ? overId : findColumnForTask(overId);

    if (!fromCol || !toCol) return;

    if (fromCol !== toCol) {
      setLocalTasks(prev => prev.map(t =>
        t.id === activeTaskId ? { ...t, statusId: toCol } : t,
      ));
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);

    if (!over || !active) {
      setIsOptimistic(false);
      return;
    }

    const taskId = active.id as string;
    const overId = over.id as string;
    const isOverColumn = sortedStatuses.some(s => s.id === overId);
    const targetCol = isOverColumn ? overId : findColumnForTask(overId);

    if (!targetCol) {
      setIsOptimistic(false);
      return;
    }

    const colItems = (columnTasks.get(targetCol) ?? []).filter(t => t.id !== taskId);
    let insertIndex = colItems.length;

    if (!isOverColumn) {
      const overIndex = colItems.findIndex(t => t.id === overId);
      if (overIndex >= 0) insertIndex = overIndex;
    }

    const beforeTaskId = insertIndex < colItems.length ? colItems[insertIndex]?.id : undefined;
    const afterTaskId = insertIndex > 0 ? colItems[insertIndex - 1]?.id : undefined;

    const originalTask = tasks.find(t => t.id === taskId);
    const sameColumn = originalTask?.statusId === targetCol;
    if (sameColumn && colItems.length === 0) {
      setIsOptimistic(false);
      return;
    }

    moveMutation.mutate({
      taskId,
      statusId: targetCol,
      beforeTaskId,
      afterTaskId,
    });
  }

  function handleDragCancel() {
    setActiveId(null);
    setIsOptimistic(false);
  }

  if (!canEdit) {
    return (
      <div className="flex-1 overflow-x-auto pb-4 px-1" data-testid="task-kanban-board">
        <div className="flex gap-4">
          {sortedStatuses.map(status => {
            const col = columnTasks.get(status.id) ?? [];
            return (
              <div key={status.id} className="flex flex-col shrink-0 w-[300px]" data-testid={`task-kanban-column-${status.id}`}>
                <div className="flex items-center gap-2 mb-3 px-1">
                  <h3 className="text-sm font-semibold" data-testid={`task-kanban-column-title-${status.id}`}>
                    {status.name}
                  </h3>
                  <Badge variant="secondary" className="text-xs" data-testid={`task-kanban-column-count-${status.id}`}>
                    {col.length}
                  </Badge>
                </div>
                <ScrollArea className="h-[calc(100vh-320px)]">
                  <div className="space-y-2 p-1 min-h-[60px]">
                    {col.length === 0 ? (
                      <div
                        className="flex items-center justify-center h-[60px] border-2 border-dashed rounded-md text-muted-foreground text-sm"
                        data-testid={`task-kanban-empty-${status.id}`}
                      >
                        No tasks
                      </div>
                    ) : (
                      col.map(task => (
                        <TaskCardContent
                          key={task.id}
                          task={task}
                          canEdit={false}
                          statuses={statuses}
                          members={members}
                          onTaskClick={onTaskClick}
                          onChangeStatus={() => {}}
                          onChangeAssignee={() => {}}
                          onArchive={() => {}}
                        />
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex-1 overflow-x-auto pb-4 px-1" data-testid="task-kanban-board">
        <div className="flex gap-4">
          {sortedStatuses.map(status => (
            <KanbanColumn
              key={status.id}
              status={status}
              tasks={columnTasks.get(status.id) ?? []}
              canEdit={canEdit}
              statuses={sortedStatuses}
              members={members}
              onTaskClick={onTaskClick}
              onCreateTask={onCreateTask}
              onChangeStatus={handleChangeStatus}
              onChangeAssignee={handleChangeAssignee}
              onArchive={handleArchive}
              onDuplicate={onDuplicate}
            />
          ))}
        </div>
      </div>

      <DragOverlay>
        {activeTask ? (
          <div className="w-[280px]">
            <TaskCardContent
              task={activeTask}
              canEdit={false}
              statuses={sortedStatuses}
              members={members}
              onTaskClick={() => {}}
              onChangeStatus={() => {}}
              onChangeAssignee={() => {}}
              onArchive={() => {}}
              isOverlay
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
